import { useMutation, useQueries, useQuery } from '@tanstack/react-query'

import {
  fetchAuditLog,
  fetchInvoiceLines,
  fetchItemSales,
  fetchSalesRegister,
  fetchSalesSummary,
  fetchReportWorkbook,
  fetchSettlement,
} from '../api'
import type { ReportExport } from '../api'
import { openExcelPreview, previewExcel } from '../components/excel-preview-store'
import type { ExcelReport } from '../components/excel-workbook'
import type {
  AuditLogQuery,
  ItemSalesQuery,
  ItemSalesReport,
  ReportQuery,
  SalesSummaryQuery,
} from '../types'

export const reportsKey = ['reports'] as const

/**
 * One report, read for one period.
 *
 * No polling. A report is read to settle a question about a period, and the
 * server prints when it read the figures and whether the last day is still
 * trading — a table that quietly changed under a manager mid-reconciliation
 * would be worse than one that says how old it is and offers a refresh.
 *
 * The previous answer stays on screen while the next period loads, dimmed, so a
 * change of filter does not blank the table and shove the page about.
 */
function useReport<T>(name: string, query: object, fetcher: () => Promise<T>) {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...reportsKey, name, query],
    queryFn: fetcher,
    placeholderData: (previous) => previous,
  })

  return {
    report: data ?? null,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

export function useSalesSummary(query: SalesSummaryQuery) {
  return useReport('sales-summary', query, () => fetchSalesSummary(query))
}

export function useSalesRegister(query: ReportQuery) {
  return useReport('sales-register', query, () => fetchSalesRegister(query))
}

export function useItemSales(query: ItemSalesQuery) {
  return useReport('item-sales', query, () => fetchItemSales(query))
}

/**
 * Item sales, one report per outlet — the legacy "Sales By Item Summary (By
 * Location)".
 *
 * Asked of the server once per outlet rather than split in the browser: the
 * item report spreads each order's discount across its lines, and only the
 * server holds the lines to spread it over. Each outlet's report therefore adds
 * up to that outlet's own sales summary to the cent, which a split of the
 * all-outlet report would not.
 *
 * Shares its cache with `useItemSales`, so the outlet already open in Item
 * sales is not read twice.
 */
export function useItemSalesByBranch(query: ReportQuery, branchIds: string[]) {
  return useQueries({
    queries: branchIds.map((branchId) => {
      const branchQuery = { ...query, branchIds: [branchId] }

      return {
        queryKey: [...reportsKey, 'item-sales', branchQuery],
        queryFn: () => fetchItemSales(branchQuery),
        placeholderData: (previous: ItemSalesReport | undefined) => previous,
      }
    }),
    combine: (results) => ({
      reports: results.map((result, index) => ({
        branchId: branchIds[index],
        report: result.data ?? null,
      })),
      isLoading: results.some((result) => result.isPending),
      isRefreshing: results.some((result) => result.isFetching && !result.isPending),
      error: results.find((result) => result.error)?.error ?? null,
      retry: () => results.forEach((result) => void result.refetch()),
    }),
  })
}

/** Every line of every sale in the period — what the details reports are cut from. */
export function useInvoiceLines(query: ReportQuery) {
  return useReport('invoice-lines', query, () => fetchInvoiceLines(query))
}

export function useSettlement(query: ReportQuery) {
  return useReport('settlement', query, () => fetchSettlement(query))
}

export function useAuditLog(query: AuditLogQuery) {
  return useReport('audit-log', query, () => fetchAuditLog(query))
}

/**
 * The Excel export, as a mutation so the button can show it is working. The
 * fetched workbook opens in the preview; saving waits for the merchant.
 */
export function useReportExport(report: ReportExport) {
  return useMutation({
    mutationFn: async (query: ReportQuery) => openExcelPreview(await fetchReportWorkbook(report, query)),
  })
}

/**
 * The Excel export of a sheet built here from what is on screen — for a report
 * the server has no workbook for, such as the register in blocks.
 */
export function useSheetExport() {
  return useMutation({ mutationFn: (sheet: ExcelReport) => previewExcel(sheet) })
}
